import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";

const AUTH_LEVELS = [
  ["inspection_only", "Inspection Only"],
  ["inspection_and_estimate", "Inspection & Estimate"],
  ["limited_disassembly", "Limited Disassembly"],
  ["insurance_approved", "Insurance Approved Repairs"],
  ["customer_pay", "Customer Pay"],
  ["custom_project", "Custom Project"],
  ["no_repairs_yet", "No Repairs Yet (hold)"],
];

/**
 * Send Vehicle Drop-Off & Repair Authorization panel.
 *
 * Layout:
 *   1) Read-only "Customer & Vehicle" summary card (pulled from the estimate/job).
 *      Missing fields show "not provided"; Cody can click "Add / edit missing info"
 *      to inline-edit VIN, mileage, plate, insurance — those changes save back to
 *      the source (estimate) and are also carried into the agreement snapshot.
 *   2) "Your authorization decision" — the only fields Cody actually fills in:
 *      auth level, authorized amount, financial breakdown, scope, token TTL, note.
 *   3) Warn-but-allow banner if the summary is missing critical fields.
 */
export default function AuthorizationPanel({ source, sourceType = "estimate", onSent, onSourceUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [existing, setExisting] = useState([]);

  // "Summary" holds the customer/vehicle/insurance facts (from estimate) that
  // Cody can OPTIONALLY edit. Cody never edits customer name/email/phone here.
  const [summary, setSummary] = useState(() => defaultSummary(source, sourceType));
  // "Terms" holds Cody's decision-only fields.
  const [terms, setTerms] = useState(() => defaultTerms());

  useEffect(() => {
    setSummary(defaultSummary(source, sourceType));
    setTerms(defaultTerms());
    if (source?.reference) {
      api
        .get(`/admin/authorizations?estimate_reference=${encodeURIComponent(source.reference)}`)
        .then((r) => setExisting(r.data || []))
        .catch(() => setExisting([]));
    }
  }, [source?.reference, sourceType]);

  // Missing-fields warning list — used to show a warn-but-allow banner.
  const missing = useMemo(() => {
    const m = [];
    if (!summary.vehicle_year) m.push("year");
    if (!summary.vehicle_make) m.push("make");
    if (!summary.vehicle_model) m.push("model");
    if (!summary.vin) m.push("VIN");
    if (!summary.mileage) m.push("mileage");
    if (!summary.insurance_company) m.push("insurance company");
    if (!summary.claim_number) m.push("claim #");
    return m;
  }, [summary]);

  const updateSummary = (k, v) => setSummary((s) => ({ ...s, [k]: v }));
  const updateTerms = (k, v) => setTerms((t) => ({ ...t, [k]: v }));

  // Persist Cody's summary edits back onto the source estimate/job.
  const saveSummaryEdits = async () => {
    if (sourceType !== "estimate") { setEditingSummary(false); return; }
    setSavingSummary(true);
    try {
      const patch = {
        vehicle_year: summary.vehicle_year || null,
        vehicle_make: summary.vehicle_make || null,
        vehicle_model: summary.vehicle_model || null,
        vin: summary.vin || null,
        license_plate: summary.license_plate || null,
        plate_state: summary.plate_state || null,
        // Insurance
        insurance_company: summary.insurance_company || null,
        claim_number: summary.claim_number || null,
      };
      await api.patch(`/admin/estimates/${source.reference}`, patch);
      onSourceUpdated && onSourceUpdated();
      setEditingSummary(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save changes to the estimate.");
    } finally {
      setSavingSummary(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // Build the payload — always uses the LATEST summary (whether Cody edited or not).
      const payload = {
        estimate_reference: source.reference,
        // Customer (from source, non-editable in this panel)
        customer_first_name: summary.customer_first_name,
        customer_last_name: summary.customer_last_name,
        customer_email: summary.customer_email,
        customer_phone: summary.customer_phone,
        customer_address: summary.customer_address || "",
        // Vehicle (may include Cody's edits)
        vehicle_year: summary.vehicle_year || "",
        vehicle_make: summary.vehicle_make || "",
        vehicle_model: summary.vehicle_model || "",
        vin: summary.vin || "",
        license_plate: summary.license_plate || "",
        plate_state: summary.plate_state || "",
        mileage: summary.mileage || "",
        // Insurance
        insurance_company: summary.insurance_company || "",
        claim_number: summary.claim_number || "",
        estimate_number: source.reference || "",
        // Cody's decision
        proposed_scope: terms.proposed_scope,
        authorized_amount: Number(terms.authorized_amount || 0),
        deductible_amount: terms.deductible_amount === "" ? null : Number(terms.deductible_amount),
        customer_pay_amount: terms.customer_pay_amount === "" ? null : Number(terms.customer_pay_amount),
        insurer_pay_amount: terms.insurer_pay_amount === "" ? null : Number(terms.insurer_pay_amount),
        deposit_amount: terms.deposit_amount === "" ? null : Number(terms.deposit_amount),
        authorization_level: terms.authorization_level,
        token_ttl_hours: Number(terms.token_ttl_hours || 72),
        staff_message: terms.staff_message || "",
        admin_confirmed_accuracy: terms.admin_confirmed_accuracy,
      };
      const { data } = await api.post("/admin/authorizations", payload);
      setSent(data);
      setExpanded(false);
      onSent && onSent();
      const list = await api.get(`/admin/authorizations?estimate_reference=${encodeURIComponent(source.reference)}`);
      setExisting(list.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "Could not send authorization"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (agreementId) => {
    const reason = window.prompt("Reason for revoking this authorization? (required)");
    if (!reason?.trim()) return;
    try {
      await api.post(`/admin/authorizations/${agreementId}/revoke`, { reason });
      const list = await api.get(`/admin/authorizations?estimate_reference=${encodeURIComponent(source.reference)}`);
      setExisting(list.data || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const canSend =
    !busy &&
    terms.admin_confirmed_accuracy &&
    terms.proposed_scope?.trim().length > 3 &&
    Number(terms.authorized_amount || 0) > 0;

  return (
    <div className="mt-8 pt-6 border-t border-ink-700" data-testid="auth-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="label">Repair Authorization</div>
          <div className="text-chrome-300 text-xs mt-1">
            Send the customer a legally-binding e-sign link to authorize the repair scope &amp; amount.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="btn"
          data-testid="auth-panel-toggle"
        >
          {expanded ? "Close" : (existing.length ? "Send another →" : "Send authorization →")}
        </button>
      </div>

      {/* Existing agreements (if any) */}
      {existing.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="auth-panel-existing">
          <div className="label">Previous / active agreements</div>
          {existing.map((a) => (
            <div key={a.id} className="border border-ink-700 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono text-proform-red">{a.id}</div>
                <div className="label">{a.status}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 mt-1 text-chrome-300 text-xs">
                <div>Sent to: <span className="text-chrome-100 break-all">{a.customer_email}</span></div>
                <div>Sent: {a.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                {a.pdf_url && (
                  <a href={`${API.replace('/api', '')}${a.pdf_url}`} target="_blank" rel="noreferrer" className="label hover:text-proform-red" data-testid={`auth-download-${a.id}`}>Download signed PDF ↗</a>
                )}
                {a.status !== "fully_executed" && a.status !== "signed_by_customer" && a.status !== "revoked" && (
                  <button type="button" onClick={() => revoke(a.id)} className="label hover:text-proform-red">Revoke</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {sent && (
        <div className="mt-3 border border-proform-red/40 bg-proform-red/5 p-3 text-sm" data-testid="auth-sent-toast">
          <div className="label text-proform-red mb-1">Sent</div>
          <div className="text-chrome-100">Agreement <span className="font-mono">{sent.agreement_id}</span> emailed. Link expires {sent.expires_at?.slice(0, 16).replace("T", " ")} UTC.</div>
          <div className="mt-2 flex flex-wrap gap-3">
            <button type="button" onClick={() => navigator.clipboard?.writeText(sent.signing_url)} className="label hover:text-proform-red">Copy signing link</button>
          </div>
        </div>
      )}

      {expanded && (
        <form onSubmit={submit} className="mt-6 space-y-6" data-testid="auth-form">
          {/* ============= SECTION 1: READ-ONLY SUMMARY ============= */}
          <section className="border border-ink-700 p-4 md:p-5" data-testid="auth-summary-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label">Customer &amp; Vehicle (from {sourceType === "job" ? "job" : "estimate"} <span className="text-proform-red font-mono">{source.reference}</span>)</div>
                <div className="text-chrome-300 text-xs mt-1">This is what the customer submitted. Cody doesn&apos;t need to re-type any of it.</div>
              </div>
              {sourceType === "estimate" && (
                editingSummary ? (
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => { setSummary(defaultSummary(source, sourceType)); setEditingSummary(false); }} className="label hover:text-proform-red">Cancel</button>
                    <button type="button" onClick={saveSummaryEdits} disabled={savingSummary} className="btn" data-testid="auth-summary-save">{savingSummary ? "Saving…" : "Save"}</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setEditingSummary(true)} className="label hover:text-proform-red" data-testid="auth-summary-edit">
                    + Add / edit missing info
                  </button>
                )
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <FactRow label="Name" value={`${summary.customer_first_name} ${summary.customer_last_name}`.trim()} />
              <FactRow label="Email" value={summary.customer_email} mono />
              <FactRow label="Phone" value={summary.customer_phone} mono />
              <FactRow label="Address" value={summary.customer_address} />
              <EditableRow label="Year" value={summary.vehicle_year} editing={editingSummary} onChange={(v) => updateSummary("vehicle_year", v)} testid="auth-year" />
              <EditableRow label="Make" value={summary.vehicle_make} editing={editingSummary} onChange={(v) => updateSummary("vehicle_make", v)} testid="auth-make" />
              <EditableRow label="Model" value={summary.vehicle_model} editing={editingSummary} onChange={(v) => updateSummary("vehicle_model", v)} testid="auth-model" />
              <EditableRow label="VIN" value={summary.vin} editing={editingSummary} mono uppercase onChange={(v) => updateSummary("vin", v.toUpperCase())} testid="auth-vin" />
              <EditableRow label="License Plate" value={summary.license_plate} editing={editingSummary} mono uppercase onChange={(v) => updateSummary("license_plate", v.toUpperCase())} testid="auth-plate" />
              <EditableRow label="Plate State" value={summary.plate_state} editing={editingSummary} uppercase onChange={(v) => updateSummary("plate_state", v.toUpperCase())} testid="auth-plate-state" />
              <EditableRow label="Mileage" value={summary.mileage} editing={editingSummary} onChange={(v) => updateSummary("mileage", v)} testid="auth-mileage" />
              <EditableRow label="Insurance Co" value={summary.insurance_company} editing={editingSummary} onChange={(v) => updateSummary("insurance_company", v)} testid="auth-insurance-co" />
              <EditableRow label="Claim #" value={summary.claim_number} editing={editingSummary} mono onChange={(v) => updateSummary("claim_number", v)} testid="auth-claim" />
            </div>
          </section>

          {/* Missing-info warn banner (warn but allow) */}
          {missing.length > 0 && (
            <div className="border border-amber-400/50 bg-amber-400/5 text-amber-200 p-4 text-sm" data-testid="auth-missing-warn">
              <div className="font-mono text-[11px] tracking-[0.24em] uppercase mb-1">Missing info</div>
              <div>The customer didn&apos;t provide: <strong>{missing.join(", ")}</strong>. You can still send this authorization — those fields will show &quot;not provided&quot; on the PDF and can be filled in later. Or click &quot;+ Add / edit missing info&quot; above to fill any in now.</div>
            </div>
          )}

          {/* ============= SECTION 2: CODY'S DECISION ============= */}
          <section className="border border-ink-700 p-4 md:p-5">
            <div className="label mb-1">Your Authorization Decision</div>
            <div className="text-chrome-300 text-xs mb-5">These are the only fields you fill in. The customer will see &amp; sign these terms.</div>

            <div className="space-y-4">
              <div>
                <label className="field-label">Authorization level *</label>
                <select className="field" value={terms.authorization_level} onChange={(e) => updateTerms("authorization_level", e.target.value)} data-testid="auth-level">
                  {AUTH_LEVELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="field-label">Authorized scope *</label>
                <textarea required rows={4} className="field" placeholder="Describe the exact work being authorized — e.g. Front bumper replacement, headlight R&I, blend adjacent panels…"
                  value={terms.proposed_scope} onChange={(e) => updateTerms("proposed_scope", e.target.value)} data-testid="auth-scope" />
                <div className="label mt-1 text-chrome-300 normal-case tracking-normal">This appears verbatim on the customer&apos;s signing page &amp; the final PDF.</div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Authorized amount * ($)</label>
                  <input required type="number" step="0.01" min="0" className="field" value={terms.authorized_amount}
                    onChange={(e) => updateTerms("authorized_amount", e.target.value)} data-testid="auth-amount" />
                  <div className="label mt-1 text-chrome-300 normal-case tracking-normal">Ceiling of what you&apos;re authorized to bill.</div>
                </div>
                <div>
                  <label className="field-label">Deposit required ($)</label>
                  <input type="number" step="0.01" min="0" className="field" value={terms.deposit_amount}
                    onChange={(e) => updateTerms("deposit_amount", e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <label className="field-label">Deductible ($)</label>
                  <input type="number" step="0.01" min="0" className="field" value={terms.deductible_amount}
                    onChange={(e) => updateTerms("deductible_amount", e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <label className="field-label">Customer pay ($)</label>
                  <input type="number" step="0.01" min="0" className="field" value={terms.customer_pay_amount}
                    onChange={(e) => updateTerms("customer_pay_amount", e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <label className="field-label">Insurer pay ($)</label>
                  <input type="number" step="0.01" min="0" className="field" value={terms.insurer_pay_amount}
                    onChange={(e) => updateTerms("insurer_pay_amount", e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <label className="field-label">Signing link expires (hours)</label>
                  <input type="number" min="1" max="336" className="field" value={terms.token_ttl_hours}
                    onChange={(e) => updateTerms("token_ttl_hours", e.target.value)} />
                  <div className="label mt-1 text-chrome-300 normal-case tracking-normal">Default 72h. Longer if you know the customer travels.</div>
                </div>
              </div>

              <div>
                <label className="field-label">Personal note (optional — appears in the email)</label>
                <textarea rows={2} className="field" placeholder="e.g. Thanks for choosing ProForm — this authorization covers the front-bumper repair we discussed."
                  value={terms.staff_message} onChange={(e) => updateTerms("staff_message", e.target.value)} />
              </div>
            </div>
          </section>

          <label className="flex items-start gap-3 text-sm text-chrome-100 border border-ink-700 p-4 cursor-pointer bg-ink-900" data-testid="auth-confirm-accuracy">
            <input type="checkbox" className="mt-1 h-5 w-5 accent-proform-red" checked={terms.admin_confirmed_accuracy}
              onChange={(e) => updateTerms("admin_confirmed_accuracy", e.target.checked)} />
            <span>I confirm the customer, vehicle, amounts, scope, and authorization level shown above are accurate. <span className="text-chrome-300">This is required — the customer will rely on these terms.</span></span>
          </label>

          {error && <div className="border border-proform-red bg-proform-red/10 text-proform-red p-3 text-sm" data-testid="auth-form-error">{error}</div>}

          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setExpanded(false)} className="label hover:text-proform-red">Cancel</button>
            <button
              type="submit"
              disabled={!canSend}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-send-btn"
            >
              {busy ? "Sending…" : "Approve & Send Authorization"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---- Small view helpers ----
function FactRow({ label, value, mono = false }) {
  const shown = (value ?? "").toString().trim();
  return (
    <div>
      <div className="label">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : ""} ${shown ? "text-chrome-100" : "text-chrome-300 italic"}`}>
        {shown || "not provided"}
      </div>
    </div>
  );
}

function EditableRow({ label, value, editing, mono = false, uppercase = false, onChange, testid }) {
  const shown = (value ?? "").toString().trim();
  if (!editing) {
    return (
      <div>
        <div className="label">{label}</div>
        <div className={`${mono ? "font-mono text-xs" : ""} ${uppercase ? "uppercase" : ""} ${shown ? "text-chrome-100" : "text-chrome-300 italic"}`}>
          {shown || "not provided"}
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="label block">{label}</label>
      <input
        className={`field ${mono ? "font-mono" : ""} ${uppercase ? "uppercase" : ""}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

// ---- Default state builders ----
function defaultSummary(source, sourceType) {
  if (!source) return {};
  if (sourceType === "job") {
    return {
      customer_first_name: (source.customer_name || "").split(" ")[0] || "",
      customer_last_name: (source.customer_name || "").split(" ").slice(1).join(" ") || "",
      customer_email: source.customer_email || "",
      customer_phone: source.customer_phone || "",
      customer_address: "",
      vehicle_year: source.vehicle_year || "",
      vehicle_make: source.vehicle_make || "",
      vehicle_model: source.vehicle_model || "",
      vin: source.vin || "",
      license_plate: "",
      plate_state: "",
      mileage: "",
      insurance_company: source.insurance_company || "",
      claim_number: source.claim_number || "",
    };
  }
  // Estimate
  return {
    customer_first_name: source.first_name || "",
    customer_last_name: source.last_name || "",
    customer_email: source.email || "",
    customer_phone: source.phone || "",
    customer_address: source.mailing_address || "",
    vehicle_year: source.vehicle_year || "",
    vehicle_make: source.vehicle_make || "",
    vehicle_model: source.vehicle_model || "",
    vin: source.vin || "",
    license_plate: source.license_plate || "",
    plate_state: source.plate_state || "",
    mileage: source.mileage || "",
    insurance_company: source.insurance_company || "",
    claim_number: source.claim_number || "",
  };
}

function defaultTerms() {
  return {
    authorization_level: "inspection_and_estimate",
    proposed_scope: "",
    authorized_amount: "",
    deductible_amount: "",
    customer_pay_amount: "",
    insurer_pay_amount: "",
    deposit_amount: "",
    token_ttl_hours: 72,
    staff_message: "",
    admin_confirmed_accuracy: false,
  };
}
